import {
  Checkbox,
  FormControlLabel,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from '@mui/material';
import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import { ControlledDatePicker } from 'components/shared/ControlledFields';
import { useTranslation } from 'react-i18next';
import { mutations } from 'RestAPIs/rapini';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import showError from 'components/shared/showError';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { DayLimitArrangementFormValues } from '../types';
import { useCreateEditForm } from '../hooks/useCreateEditForm';
import { Controller } from 'react-hook-form';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import DrawerFooter from 'components/shared/Drawer/DrawerFooter';
import { LoadingButton } from '@mui/lab';
import { StyledFormFieldsBox } from './CreateEditForm.styles';
import { DayLimitArrangementForEmployeeResponse } from 'generated';

type CreateEditFormProps =
  FormComponentProps<DayLimitArrangementForEmployeeResponse> & {
    employeeId: number;
  };

export default function CreateEditForm({
  rowData,
  handleClose,
  employeeId
}: CreateEditFormProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const isEditMode = Boolean(rowData?.id);

  const { control, handleSubmit, watch, formState, ...restMethods } =
    useCreateEditForm(rowData);

  const { errors } = formState;
  const isNotApplicable = watch('dayLimitArrangementNotApplicableForEmployee');
  const alternativePeriod = watch('alternativePeriod');
  const alternativeTimeUnit = watch('alternativeTimeUnit');
  const validFrom = watch('validFrom');
  const validTo = watch('validTo');

  const createMutation = mutations.useCreateDayLimitArrangementForEmployee({
    onSuccess: () => {
      showSuccessSnackbar();
      queryClient.invalidateQueries(['getDayLimitArrangementsForEmployee']);
      handleClose();
    },
    onError: (error) => {
      showError(error as any);
    }
  });

  const updateMutation = mutations.useUpdateDayLimitArrangementForEmployee({
    onSuccess: () => {
      showSuccessSnackbar();
      queryClient.invalidateQueries(['getDayLimitArrangementsForEmployee']);
      handleClose();
    },
    onError: (error) => {
      showError(error as any);
    }
  });

  const onSubmit = (data: DayLimitArrangementFormValues) => {
    const payload = {
      ...(data.id && { id: data.id }),
      employeeId,
      validFrom: dayjs(data.validFrom).format('YYYY-MM-DD'),
      validTo: dayjs(data.validTo).format('YYYY-MM-DD'),
      maxNumberOfDays: data.dayLimitArrangementNotApplicableForEmployee
        ? undefined
        : data.maxNumberOfDays,
      dayLimitArrangementNotApplicableForEmployee:
        data.dayLimitArrangementNotApplicableForEmployee ?? false,
      ...(data.dayLimitArrangementNotApplicableForEmployee
        ? {
            alternativeTimeUnit: undefined,
            alternativeNrOfPeriods: undefined,
            startDateForAlternativePeriod: undefined
          }
        : data.alternativePeriod && {
            alternativeTimeUnit: data.alternativeTimeUnit,
            ...(data.alternativeTimeUnit === 'CUSTOM' && {
              alternativeNrOfPeriods: data.alternativeNrOfPeriods,
              startDateForAlternativePeriod: data.startDateForAlternativePeriod
                ? dayjs(data.startDateForAlternativePeriod).format('YYYY-MM-DD')
                : undefined
            })
          })
    };

    if (isEditMode) {
      updateMutation.mutate(payload as any);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <MultiStepForm
      onSubmit={handleSubmit(onSubmit)}
      methods={{ control, handleSubmit, watch, formState, ...restMethods }}
    >
      <MultiStepForm.Section
        title={t('dayLimitArrangements.formSectionLabels.base')}
        validateFields={['validFrom', 'validTo', 'maxNumberOfDays']}
      >
        <StyledFormFieldsBox>
          <ControlledDatePicker
            control={control}
            name="validFrom"
            label={t('validFrom')}
            error={!!errors.validFrom}
            helperText={errors.validFrom?.message}
          />

          <ControlledDatePicker
            control={control}
            name="validTo"
            label={t('validTo')}
            error={!!errors.validTo}
            helperText={errors.validTo?.message}
          />

          <Controller
            name="dayLimitArrangementNotApplicableForEmployee"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t('dayLimitArrangements.notApplicable')}
              />
            )}
          />

          {!isNotApplicable && (
            <Controller
              name="maxNumberOfDays"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('dayLimitArrangements.maxNumberOfDays')}
                  type="number"
                  error={!!errors.maxNumberOfDays}
                  helperText={errors.maxNumberOfDays?.message}
                  fullWidth
                />
              )}
            />
          )}

          {!isNotApplicable && (
            <Controller
              name="alternativePeriod"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t('dayLimitArrangements.alternativePeriod')}
                />
              )}
            />
          )}

          {!isNotApplicable && alternativePeriod && (
            <>
              <Controller
                name="alternativeTimeUnit"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.alternativeTimeUnit}>
                    <FormLabel>
                      {t('dayLimitArrangements.form.alternativeTimeUnit')}
                    </FormLabel>
                    <RadioGroup
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <FormControlLabel
                        value="MONTH"
                        control={<Radio />}
                        label={t('dayLimitArrangements.form.month')}
                      />
                      <FormControlLabel
                        value="CUSTOM"
                        control={<Radio />}
                        label={t('dayLimitArrangements.form.multipleWeeks')}
                      />
                    </RadioGroup>
                    {errors.alternativeTimeUnit?.message && (
                      <span style={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                        {errors.alternativeTimeUnit.message}
                      </span>
                    )}
                  </FormControl>
                )}
              />

              {alternativeTimeUnit === 'CUSTOM' && (
                <>
                  <Controller
                    name="alternativeNrOfPeriods"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t(
                          'dayLimitArrangements.form.alternativeNrOfPeriods'
                        )}
                        type="number"
                        error={!!errors.alternativeNrOfPeriods}
                        helperText={errors.alternativeNrOfPeriods?.message}
                        fullWidth
                      />
                    )}
                  />

                  <ControlledDatePicker
                    control={control}
                    name="startDateForAlternativePeriod"
                    label={t(
                      'dayLimitArrangements.form.startDateForAlternativePeriod'
                    )}
                    minDate={
                      validFrom
                        ? dayjs(validFrom).format('YYYY-MM-DD')
                        : undefined
                    }
                    maxDate={
                      validTo ? dayjs(validTo).format('YYYY-MM-DD') : undefined
                    }
                    error={!!errors.startDateForAlternativePeriod}
                    helperText={errors.startDateForAlternativePeriod?.message}
                    shouldDisableDate={(date) => date.isoWeekday() !== 1}
                  />
                </>
              )}
            </>
          )}
        </StyledFormFieldsBox>
      </MultiStepForm.Section>

      <DrawerFooter>
        <LoadingButton
          loading={isLoading}
          type="submit"
          color="primary"
          variant="contained"
        >
          {t('save')}
        </LoadingButton>
      </DrawerFooter>
    </MultiStepForm>
  );
}
